import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MetaGrid from 'shared/components/ui/MetaGrid';
import DetailTabs from 'features/activity/components/DetailTabs';
import DetailPropertyList from 'features/activity/components/DetailPropertyList';
import {
  isBlank,
  truncateId,
  formatDateTime,
  isExternalUrl,
  userDetailPath,
  sessionDetailPath,
} from 'features/activity/utils/activityFormat';
import { openUserProfile } from 'features/users/openUserProfile';

/**
 * Event detail body, laid out like the agent-run drawer: a meta card of the
 * fields you scan first, then tabs, then the full property list.
 *
 * The old version opened with a free-form "hero" block of stacked text that
 * shared no structure with the run drawer's meta strip, so the two popups
 * looked like they came from different products.
 */
/**
 * `fill` — true inside a drawer, where the view owns the remaining height and
 * the property list scrolls internally. GroupDetailPage renders this inline in
 * an auto-height block, where a flex-1 + overflow-hidden card would have no
 * height to grow into, so it stays false there and the list grows naturally.
 */
const EventDetailView = ({ event, fill = false }) => {
  const params = useParams();
  const [activeTab, setActiveTab] = useState('all');

  if (!event) {
    return (
      <div className="px-4 py-8 text-center text-[12px] text-[color:var(--text-secondary)]">
        No event data available.
      </div>
    );
  }

  /** Render a value for the property list; null means "omit this row". */
  const formatValue = (value, keepEmpty = false) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      const isEmpty = Array.isArray(value) ? value.length === 0 : Object.keys(value).length === 0;
      if (isEmpty && !keepEmpty) return null;
      return JSON.stringify(value, null, 2);
    }
    const s = String(value);
    if (keepEmpty) return s;
    return s.trim().length > 0 ? s : null;
  };

  /** Parse JSONB fields that may arrive as an object or as a JSON string. */
  const parseJsonObject = (raw) => {
    if (raw == null) return null;
    if (typeof raw === 'string') {
      try {
        const p = JSON.parse(raw);
        return p && typeof p === 'object' && !Array.isArray(p) ? p : null;
      } catch {
        return null;
      }
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return Object.keys(raw).length > 0 ? raw : null;
    }
    return null;
  };

  // Element fields arrive either as an element_data object or as flat columns.
  const getElementData = () => {
    if (event.element_data && typeof event.element_data === 'object') return event.element_data;
    const d = {};
    if (event.element_name) d.element_name = event.element_name;
    if (event.element_id) d.element_id = event.element_id;
    if (event.element_class) d.element_class = event.element_class;
    if (event.element_tag_name) d.element_tag_name = event.element_tag_name;
    if (event.element_type) d.element_type = event.element_type;
    if (event.element_text) d.element_text = event.element_text;
    if (event.element_position) d.element_position = event.element_position;
    return Object.keys(d).length > 0 ? d : null;
  };
  const elementData = getElementData();

  const groupIdsRaw = event.group_membership_ids;
  const groupMembershipDisplay =
    groupIdsRaw == null
      ? null
      : Array.isArray(groupIdsRaw)
        ? (groupIdsRaw.length ? JSON.stringify(groupIdsRaw) : null)
        : typeof groupIdsRaw === 'string' && groupIdsRaw.trim()
          ? groupIdsRaw
          : null;

  const sessionId = event.session_uuid || event.session_id;
  const distinctId = event.distinct_id;
  const pageUrl = event.current_url || event.uri;

  /* ------------------------------------------------------------- meta card */

  const eventName = event.event_name || event.event_type || 'Event';
  const isAuto = event.event_type === 'auto';

  const metaCells = [
    // Name and type are separate cells rather than a name with a badge glued to
    // it: MetaCell truncates its value, and a trailing badge is the first thing
    // the ellipsis eats.
    {
      label: 'Event',
      title: eventName,
      content: <span className="font-mono">{eventName}</span>,
    },
    {
      label: 'Type',
      content: (
        <span style={{ color: isAuto ? 'var(--feedback-info)' : 'var(--feedback-warning)' }}>
          {isAuto ? 'Auto' : 'Custom'}
        </span>
      ),
    },
    !isBlank(event.event_category) && {
      label: 'Category',
      content: <span>{event.event_category}</span>,
    },
    event.timestamp && {
      label: 'Time',
      title: formatDateTime(event.timestamp),
      content: <span className="tabular-nums">{formatDateTime(event.timestamp)}</span>,
    },
    !isBlank(distinctId) && {
      label: 'User',
      title: distinctId,
      content: (
        <button
          type="button"
          onClick={() => openUserProfile(distinctId)}
          className="cursor-pointer border-0 bg-transparent p-0 font-mono text-[color:var(--text-primary)] underline-offset-2 hover:underline"
        >
          {truncateId(distinctId)}
        </button>
      ),
    },
    !isBlank(sessionId) && {
      label: 'Session',
      title: sessionId,
      content: (
        <Link
          to={sessionDetailPath(params.team, sessionId)}
          className="font-mono text-[color:var(--text-primary)] underline-offset-2 hover:underline"
        >
          {truncateId(sessionId)}
        </Link>
      ),
    },
    !isBlank(event.country) && {
      label: 'Location',
      title: [event.city, event.region, event.country].filter(Boolean).join(', '),
      content: <span>{[event.city, event.country].filter(Boolean).join(', ')}</span>,
    },
    !isBlank(event.os) && { label: 'OS', content: <span>{event.os}</span> },
    !isBlank(event.browser_name) && { label: 'Browser', content: <span>{event.browser_name}</span> },
    !isBlank(pageUrl) && {
      label: 'Page',
      title: pageUrl,
      content: isExternalUrl(pageUrl) ? (
        <a
          href={pageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[color:var(--text-primary)] underline-offset-2 hover:underline"
        >
          {pageUrl}
        </a>
      ) : (
        <span>{pageUrl}</span>
      ),
    },
  ].filter(Boolean);

  /* -------------------------------------------------------- property rows */

  const standardRows = [
    { label: 'Event ID', value: event.id != null ? String(event.id) : null, mono: true },
    { label: 'Event Name', value: event.event_name },
    { label: 'Event Type', value: event.event_type },
    { label: 'Event Category', value: event.event_category },
    { label: 'Timestamp', value: formatDateTime(event.timestamp) },

    {
      label: 'Session ID',
      value: sessionId,
      mono: true,
      to: sessionDetailPath(params.team, sessionId),
    },
    { label: 'User ID', value: event.user_id, mono: true },
    {
      label: 'Distinct ID',
      value: distinctId,
      mono: true,
      to: userDetailPath(params.team, distinctId),
    },
    { label: 'User identity ID', value: event.user_identity_id != null ? String(event.user_identity_id) : null, mono: true },
    { label: 'Group membership IDs', value: groupMembershipDisplay, mono: true },

    { label: 'Country', value: event.country },
    { label: 'City', value: event.city },
    { label: 'Region', value: event.region },
    { label: 'Latitude', value: event.latitude },
    { label: 'Longitude', value: event.longitude },
    { label: 'Timezone', value: event.timezone },

    { label: 'Browser', value: event.browser_name },
    { label: 'Browser Version', value: event.browser_version },
    { label: 'OS', value: event.os },
    { label: 'OS Version', value: event.os_version },
    { label: 'Device Type', value: event.device_type },
    { label: 'Language', value: event.language },
    { label: 'Screen Resolution', value: event.screen_width && event.screen_height ? `${event.screen_width}x${event.screen_height}` : null },
    { label: 'Viewport', value: event.viewport_width && event.viewport_height ? `${event.viewport_width}x${event.viewport_height}` : null },
    { label: 'DPI', value: event.dpi },

    { label: 'Page Title', value: event.page_title },
    { label: 'Current URL', value: event.current_url, href: isExternalUrl(event.current_url) ? event.current_url : null },
    { label: 'URL', value: event.uri, href: isExternalUrl(event.uri) ? event.uri : null },
    { label: 'Initial Referrer', value: event.initial_referrer, href: isExternalUrl(event.initial_referrer) ? event.initial_referrer : null },
    { label: 'Referrer', value: event.referrer, href: isExternalUrl(event.referrer) ? event.referrer : null },

    { label: 'UTM Source', value: event.utm_source },
    { label: 'UTM Medium', value: event.utm_medium },
    { label: 'UTM Campaign', value: event.utm_campaign },
    { label: 'UTM Term', value: event.utm_term },
    { label: 'UTM Content', value: event.utm_content },
    { label: 'UTM ID', value: event.utm_id },

    { label: 'Is Web3 User', value: event.is_web3_user },
    { label: 'Wallet Address', value: event.wallet_address, mono: true },

    { label: 'Element Name', value: elementData?.element_name },
    { label: 'Element ID', value: elementData?.element_id, mono: true },
    { label: 'Element Class', value: elementData?.element_class, mono: true },
    { label: 'Element Tag', value: elementData?.element_tag_name },
    { label: 'Element Type', value: elementData?.element_type },
    { label: 'Element Text', value: elementData?.element_text },
    { label: 'Element Position', value: elementData?.element_position },
  ]
    .map((row) => ({ ...row, rawValue: row.value, value: formatValue(row.value) }))
    .filter((row) => row.value !== null);

  // custom_properties (track() payloads) and auto_event_data (SDK capture) keep
  // empty strings / 0 / false — an explicitly-sent falsy value is information.
  const toPayloadRows = (obj, prefix = '') =>
    obj
      ? Object.keys(obj).map((key) => ({
          label: prefix ? `${prefix}${key}` : key,
          value: formatValue(obj[key], true),
          rawValue: obj[key],
          mono: typeof obj[key] !== 'object',
        }))
      : [];

  const isMeaningful = (row) => {
    const v = row.rawValue;
    if (v === null || v === undefined) return false;
    if (typeof v === 'string' && v.trim() === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return false;
    return true;
  };

  const customRows = toPayloadRows(parseJsonObject(event.custom_properties)).filter(isMeaningful);
  const autoRows = toPayloadRows(parseJsonObject(event.auto_event_data), 'Auto · ').filter(isMeaningful);
  const allRows = [...standardRows, ...customRows, ...autoRows];

  const tabs = [
    { id: 'all', label: 'All properties', badge: allRows.length },
    { id: 'custom', label: 'Custom', badge: customRows.length },
  ];

  return (
    <div className={`flex flex-col gap-3 ${fill ? 'min-h-0 flex-1' : ''}`}>
      {/* 4 columns, not the run drawer's 5: this panel maxes out at 880px and
          timestamps/urls were being ellipsised at 5-across. */}
      <MetaGrid cells={metaCells} columns={4} />

      <div
        className={`flex flex-col overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface ${
          fill ? 'min-h-0 flex-1' : ''
        }`}
      >
        <DetailTabs tabs={tabs} value={activeTab} onChange={setActiveTab} />
        <div className={fill ? 'min-h-0 flex-1 overflow-y-auto' : ''}>
          {activeTab === 'all' ? (
            <DetailPropertyList rows={allRows} idPrefix={`event-${event.id}`} />
          ) : (
            <DetailPropertyList
              rows={customRows}
              idPrefix={`event-custom-${event.id}`}
              emptyMessage="No custom properties on this event."
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailView;
